import React from 'react';
import PropTypes from 'prop-types';
import Yup from 'yup';
import {
  Select,
  MenuItem,
  TextField,
  Calendar,
  Radio,
  RadioItem,
  Button,
  Modal,
  ModalButtons,
  ModalContent,
  ProgressBar,
  Banner,
  Divider,
  withForm,
  DateUtils,
} from 'mw-style-react';
import { DATE_FORMAT_2, DATE_FORMAT_4 } from 'constants';
import {
  GET_ACCOUNT,
  CREATE_TRANSACTION,
  CREATE_GROUP_TRANSACTION,
} from '@control-front-end/common/constants/actorAccounts';
import useIntl from 'useIntl';
import mes from './intl';
// eslint-disable-next-line no-unused-vars
import m from '../../Modal.scss'; // NOSONAR
// eslint-disable-next-line no-unused-vars
import l from './CreateTransaction.scss'; // NOSONAR

function CreateTransactionModal(props) {
  const {
    values,
    handleSubmit,
    handleOnChange,
    visibility,
    onClose,
    isSubmit,
    errors,
  } = props;
  const t = useIntl();

  const handleChangeDate = ({ id, value }) => {
    handleOnChange({ id, value: value.startDate });
  };

  const handleChangeRange = ({ id, value }) => {
    handleOnChange({ id, value, error: false });
    const { startDate, endDate } = value;
    setTimeout(
      () =>
        handleOnChange({
          id: 'range.startDate',
          value: startDate,
          error: startDate === endDate,
        }),
      100
    );
  };

  const getStringDate = () => {
    const { startDate, endDate } = values.range;
    if (!startDate) return '';
    const start = DateUtils.toDate(startDate, DATE_FORMAT_2);
    const end = DateUtils.toDate(endDate, DATE_FORMAT_2);
    return startDate !== endDate ? `${start} - ${end}` : `${start}`;
  };

  const renderPeriodDetails = () => {
    const days = [...Array(32).keys()].slice(1).map((item) => {
      const day = item.toString();
      return <MenuItem key={day} value={day} label={day} />;
    });
    const errorText = t(mes[errors['range.startDate']]) || t(mes.invalid_range);
    return (
      <div>
        <TextField
          styleName="m.textfield l.transaction__date__value"
          size="large"
          bordered={true}
          label={t(mes.transactionPeriod)}
          value={getStringDate()}
          placeholder={t(mes.selectPeriodRange)}
          error={!!errors['range.startDate']}
          helperText={errors['range.startDate'] ? errorText : ''}
          calendar={() => (
            <Calendar
              id="range"
              styleName="l.transaction__date__calendar"
              size="small"
              dateRange={true}
              value={values.range}
              onChange={handleChangeRange}
            />
          )}
        />
        {values.period === 'day' ? (
          <Select
            id="day"
            styleName="m.select l.transaction__date__select"
            size="large"
            bordered={true}
            label={t(mes.dayEvery)}
            value={values.day}
            onChange={handleOnChange}
          >
            {days}
          </Select>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      styleName="m.modal"
      size="medium"
      onClose={onClose}
      label={t(mes.createTransaction)}
      visibility={visibility}
    >
      <ModalContent styleName="m.modal__content l.content">
        <Radio
          id="incomeType"
          styleName="l.transaction__type"
          value={values.incomeType}
          align="horizontal"
          label={t(mes.debitOrCredit)}
          onChange={handleOnChange}
        >
          <RadioItem label={t(mes.debit)} value="debit" />
          <RadioItem label={t(mes.credit)} value="credit" />
        </Radio>
        <Divider />
        <div styleName="l.transaction__date">
          <Radio
            id="period"
            value={values.period}
            align="vertical"
            label="Select transaction frequency"
            onChange={handleOnChange}
          >
            <RadioItem label={t(mes.oneTime)} value="default" />
            <RadioItem label={t(mes.monthEnd)} value="monthEnd" />
            <RadioItem label={t(mes.quarterEnd)} value="quarterEnd" />
            <RadioItem label={t(mes.fixedDay)} value="day" />
          </Radio>
          {values.period === 'default' ? (
            <TextField
              styleName="m.textfield l.transaction__date__value"
              size="large"
              bordered={true}
              label={t(mes.transactionDate)}
              value={
                values.createdAt
                  ? DateUtils.toDate(values.createdAt, DATE_FORMAT_4)
                  : ''
              }
              error={!!errors.createdAt}
              calendar={() => (
                <Calendar
                  id="createdAt"
                  styleName="l.transaction__date__calendar"
                  size="small"
                  time={true}
                  value={{ startDate: values.createdAt }}
                  onChange={handleChangeDate}
                />
              )}
            />
          ) : (
            renderPeriodDetails()
          )}
        </div>
        {values.period === 'day' && values.day === '31' ? (
          <Banner
            styleName="l.transaction__date__banner"
            size="medium"
            value={t(mes.day30Info)}
          />
        ) : null}
        <Divider />
        <TextField
          id="amount"
          styleName="m.textfield l.transaction__amount"
          size="large"
          type="float"
          label={t(mes.amount)}
          bordered={true}
          autoFocus={true}
          value={values.amount || ''}
          error={!!errors.amount}
          helperText={t(mes[errors.amount])}
          onChange={handleOnChange}
        />
        <TextField
          id="comment"
          styleName="m.textfield l.transaction__comment"
          size="large"
          label={t(mes.comment)}
          bordered={true}
          value={values.comment || ''}
          error={!!errors.comment}
          helperText={t(mes[errors.comment])}
          onChange={handleOnChange}
        />
      </ModalContent>
      <ModalButtons styleName="m.modal__buttons">
        <Button
          label={t(mes.save)}
          size="large"
          onClick={handleSubmit}
          visibility={isSubmit ? 'disabled' : 'visible'}
        />
        <ProgressBar
          styleName="m.modal__loader"
          type="circle"
          size="small"
          visibility={isSubmit ? 'visible' : 'hidden'}
        />
      </ModalButtons>
    </Modal>
  );
}

CreateTransactionModal.propTypes = {
  visibility: PropTypes.bool,
  onClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleOnChange: PropTypes.func,
  isSubmit: PropTypes.bool,
  values: PropTypes.object,
  errors: PropTypes.object,
};

const CreateTransaction = withForm(
  {
    mapPropsToValues() {
      return {
        incomeType: 'debit',
        period: 'default',
        day: '1',
        createdAt: DateUtils.unixtime(),
        amount: 0,
        comment: '',
        range: {},
      };
    },
    yup: Yup.object().shape({
      amount: Yup.number().notOneOf([0], 'not_zero').required('field_required'),
      period: Yup.string(),
      day: Yup.number().min(1).max(31),
      range: Yup.object().when('period', {
        is: (period) => period !== 'default',
        then: Yup.object().shape({
          startDate: Yup.number().required('field_required'),
        }),
      }),
    }),
    handleSubmit: (values, dispatch, formActions, props) => {
      const { debitId, creditId, key, accountType } = props.data;
      const { period, incomeType, amount, comment } = values;
      const id = values.incomeType === 'debit' ? debitId : creditId;
      const transaction = { amount: +amount, comment };
      const isGroup = period !== 'default';
      if (isGroup) {
        const { startDate, endDate } = values.range;
        transaction.period = period;
        transaction.startDate = DateUtils.dateToUnixtime(
          DateUtils.startOf(startDate * 1000, 'day')
        );
        transaction.endDate = DateUtils.dateToUnixtime(
          DateUtils.endOf(endDate * 1000, 'day')
        );
      } else {
        transaction.createdAt = values.createdAt * 1000;
      }
      if (period === 'day') {
        transaction.day = parseInt(values.day, 10);
      }
      dispatch({
        type: isGroup
          ? CREATE_GROUP_TRANSACTION.REQUEST
          : CREATE_TRANSACTION.REQUEST,
        payload: { id, key, accountType, incomeType, transaction },
        callback: () => {
          if (!isGroup) return;
          dispatch({
            type: GET_ACCOUNT.REQUEST,
            payload: { id, key, accountType },
          });
        },
      });
      props.onClose();
    },
  },
  CreateTransactionModal
);

export default CreateTransaction;
